import { useState } from 'react';
import { AlertCircle, ClipboardCheck } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/atoms/alert';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/atoms/sheet';
import {
  FormDefinitionBuilder,
  type FormDefinitionBuilderValue,
} from '@/components/appointment-forms/FormDefinitionBuilder';
import { useCreateFormTemplate } from '@/hooks/forms-engine/useFormTemplates';
import {
  buildCreateTemplateInput,
  createUnifiedBuilderValue,
  locateUnifiedBuilderIssues,
  type BuilderIssue,
} from '@/lib/forms/unified-form-builder';
import { RadioGroup, RadioGroupItem } from '@/components/atoms/radio-group';
import { Label } from '@/components/atoms/label';
import { Switch } from '@/components/atoms/switch';
import type { FormSurface, FormTemplate } from '@/types/formsEngine';

/** What the person creating it decided about the link it is created for. */
export interface CreatedLinkDefaults {
  blocksCompletion: boolean;
}

interface UnifiedChecklistCreateSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (template: FormTemplate, defaults: CreatedLinkDefaults) => void;
  /**
   * Ask whether the link this is created for blocks completion. Only where
   * the sheet is creating for a link — a job type — since a template holds no
   * such setting of its own.
   */
  askRequired?: boolean;
  /**
   * Which kind of template this builds. The builder, its validation and the
   * create payload were already parameterised by surface — only this sheet
   * pinned them to CHECKLIST, which is why a form could not be created the
   * same way. Defaults to CHECKLIST so the job-type drawer is unchanged.
   */
  surface?: FormSurface;
}

export function UnifiedChecklistCreateSheet({
  open,
  onOpenChange,
  onCreated,
  surface = 'CHECKLIST',
  askRequired = false,
}: UnifiedChecklistCreateSheetProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        showCloseButton={false}
        className="w-full max-w-none gap-0 overflow-hidden p-0 sm:max-w-[94vw] xl:max-w-[1320px]"
      >
        {open ? (
          <UnifiedChecklistCreateEditor
            surface={surface}
            askRequired={askRequired}
            onCancel={() => onOpenChange(false)}
            onCreated={onCreated}
          />
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

function UnifiedChecklistCreateEditor({
  surface,
  askRequired,
  onCancel,
  onCreated,
}: {
  surface: FormSurface;
  askRequired: boolean;
  onCancel: () => void;
  onCreated: (template: FormTemplate, defaults: CreatedLinkDefaults) => void;
}) {
  const createTemplate = useCreateFormTemplate();
  const [value, setValue] = useState<FormDefinitionBuilderValue>(() =>
    createUnifiedBuilderValue(surface),
  );
  const [issues, setIssues] = useState<BuilderIssue[]>([]);
  const [required, setRequired] = useState(false);
  const kind = surface === 'CHECKLIST' ? 'checklist' : 'form';

  function save() {
    // Located rather than listed: the builder puts each one on the field that
    // caused it and brings the first into view, so the refusal is visible from
    // wherever the person pressing this button happens to be looking.
    const nextIssues = locateUnifiedBuilderIssues(value, surface);
    if (nextIssues.length > 0) {
      setIssues(nextIssues);
      return;
    }

    setIssues([]);
    createTemplate.mutate(
      {
        template: buildCreateTemplateInput(value, surface),
        // The Job Type form owns the association. It is persisted only when
        // the user saves the parent form.
        assignments: [],
      },
      {
        onSuccess: (template) => {
          onCreated(template, { blocksCompletion: required });
          onCancel();
        },
      },
    );
  }

  const itemCount = value.sections.reduce((total, section) => total + section.items.length, 0);

  return (
    <>
      <SheetHeader className="border-b px-5 py-4 text-left">
        <div className="flex items-center gap-2">
          <SheetTitle>Create {kind}</SheetTitle>
          <Badge variant="outline" className="gap-1.5 text-[10px]">
            <ClipboardCheck className="h-3 w-3" />
            Unified form
          </Badge>
        </div>
        <SheetDescription>
          {value.sections.length} {value.sections.length === 1 ? 'section' : 'sections'} ·{' '}
          {itemCount} {itemCount === 1 ? 'item' : 'items'}
        </SheetDescription>
      </SheetHeader>

      <div className="min-h-0 flex-1 overflow-y-auto bg-muted/20 px-4 py-4 sm:px-5">
        <div className="mx-auto w-full max-w-[1240px] space-y-3">
          {issues.length > 0 ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>
                Fix {issues.length} {issues.length === 1 ? 'issue' : 'issues'} before saving
              </AlertTitle>
              <AlertDescription>
                <ul className="list-disc space-y-1 pl-4">
                  {issues.slice(0, 8).map((issue) => (
                    <li key={issue.message}>{issue.message}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          ) : null}

          {/* Asked before the questions are written, because the answer
              narrows what may be written: a customer-filled template runs on
              the public runtime, which takes fewer item types and no repeating
              sections. */}
          <div className="rounded-lg border bg-background p-4">
            <p className="text-sm font-medium">Completed by</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {value.completedBy === 'CUSTOMER'
                ? 'The customer fills this in from a link. Repeating sections and field types the public form cannot render are unavailable.'
                : 'The technician fills this in on the visit, and it works offline.'}
            </p>
            <RadioGroup
              className="mt-3 flex gap-6"
              value={value.completedBy ?? 'TECHNICIAN'}
              onValueChange={(next) =>
                setValue((current) => ({
                  ...current,
                  completedBy: next as 'TECHNICIAN' | 'CUSTOMER',
                }))
              }
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="TECHNICIAN" id="completed-by-technician" />
                <Label htmlFor="completed-by-technician" className="font-normal">
                  Technician on site
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="CUSTOMER" id="completed-by-customer" />
                <Label htmlFor="completed-by-customer" className="font-normal">
                  Customer
                </Label>
              </div>
            </RadioGroup>
            {askRequired ? (
              <div className="mt-4 flex items-start justify-between gap-3 border-t pt-4">
                <div className="min-w-0">
                  <Label htmlFor="create-required" className="text-sm font-medium">
                    Required to complete
                  </Label>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    The job or visit cannot be completed until this is done. You can change it on
                    the job type afterwards.
                  </p>
                </div>
                <Switch id="create-required" checked={required} onCheckedChange={setRequired} />
              </div>
            ) : null}
          </div>

          <FormDefinitionBuilder
            value={value}
            surface={surface}
            issues={issues}
            onChange={(next) => {
              setValue(next);
              if (issues.length > 0) setIssues([]);
            }}
          />
        </div>
      </div>

      <SheetFooter className="flex-row justify-end border-t px-5 py-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="button" disabled={createTemplate.isPending} onClick={save}>
          {createTemplate.isPending ? 'Creating...' : `Create ${kind}`}
        </Button>
      </SheetFooter>
    </>
  );
}
